/**
 * Existing-memory retrieval: feed the dream prompt the
 * standing memory + index so the model can detect contradiction-with-standing-
 * memory (not just within-window) and apply the durable-vs-inbox recurrence bar.
 * Also exposes origin-per-file so the apply gate can route the verb:
 * worker/dream => editable; operator/seed => flag-only. Absent origin ⇒ worker.
 */
import type { BrainRepoWriter, MemoryFile } from "./types";

export interface IndexEntry { path: string; title: string; summary: string }

export interface MemoryContext {
  files: MemoryFile[];
  indexEntries: IndexEntry[];
  /** Origin tier of the target file; absent frontmatter ⇒ "worker". */
  originOf(path: string): string;
}

/** Index grammar: `- \`memory/<file>.md\` — **<title>**: <summary>. (<date> · <tags>)` (AGENTS.md §Index formats). */
const INDEX_LINE = /^-\s+`([^`]+)`\s+—\s+\*\*(.+?)\*\*:\s+(.*)$/;

export function parseMemoryIndex(index: string): IndexEntry[] {
  const out: IndexEntry[] = [];
  for (const line of index.split("\n")) {
    const m = INDEX_LINE.exec(line);
    if (m) out.push({ path: m[1], title: m[2].trim(), summary: m[3].trim() });
  }
  return out;
}

export async function loadMemoryContext(brain: BrainRepoWriter): Promise<MemoryContext> {
  const [files, indexRaw] = await Promise.all([brain.listMemory(), brain.readFile("memory/MEMORY.md")]);
  const indexEntries = indexRaw ? parseMemoryIndex(indexRaw) : [];
  const originByPath = new Map<string, string>();
  for (const f of files) {
    const o = f.frontmatter.origin;
    originByPath.set(f.path, typeof o === "string" ? o : "worker");
  }
  return {
    files,
    indexEntries,
    originOf: (path: string) => originByPath.get(path) ?? "worker",
  };
}
